import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Check, ImagePlus, Loader2, X } from 'lucide-react';
import { Sheet, QtyStepper, Stars } from '@/components/shop/ShopUI';
import { Img } from '@/components/ui';
import { cancelOrderItems, requestReturn, submitReview } from '@/api/shop';
import { uploadAttachment } from '@/api/support';
import { friendlyError } from '@/lib/apiError';
import { RETURN_REASONS, activeQty, newIdempotencyKey, rupees } from '@/lib/shop';

/* -------------------------------------------------------------- Cancel */

/** Pick which items (and how many of each) to cancel from one shipment. */
export const CancelSheet = ({ open, onClose, orderId, items = [], onDone }) => {
  const cancellable = useMemo(() => items.filter((i) => activeQty(i) > 0), [items]);
  const [pick, setPick] = useState({});
  const [busy, setBusy] = useState(false);
  const [key, setKey] = useState(null);

  useEffect(() => {
    if (!open) return;
    // Everything ticked at full quantity by default — the usual intent.
    setPick(Object.fromEntries(cancellable.map((i) => [i.id, activeQty(i)])));
    setKey(newIdempotencyKey('cancel'));
  }, [open, cancellable]);

  const chosen = cancellable.filter((i) => pick[i.id] > 0);
  const refund = chosen.reduce((s, i) => s + i.unitPrice * pick[i.id], 0);

  const submit = async () => {
    if (!chosen.length) return;
    setBusy(true);
    try {
      const res = await cancelOrderItems(orderId, {
        idempotencyKey: key,
        items: chosen.map((i) => ({ orderItemId: i.id, qty: pick[i.id] })),
      });
      toast.success(res?.message === 'Cancelled' ? 'Items cancelled. Your refund is on its way.' : res?.message || 'Cancelled');
      onDone();
      onClose();
    } catch (err) {
      toast.error(friendlyError(err, 'Could not cancel'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Cancel items"
      footer={
        <div className="flex items-center gap-3">
          <div className="flex-1 text-sm">
            <p className="text-ink-500">Refund</p>
            <p className="tabular font-bold text-ink-900">{rupees(refund)}{chosen.length === cancellable.length && chosen.every((i) => pick[i.id] === activeQty(i)) ? ' + delivery' : ''}</p>
          </div>
          <button type="button" onClick={submit} disabled={busy || !chosen.length} className="lk-btn flex-1 bg-danger text-white hover:bg-danger/90">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Cancel {chosen.reduce((s, i) => s + pick[i.id], 0) || ''} item{chosen.reduce((s, i) => s + pick[i.id], 0) === 1 ? '' : 's'}
          </button>
        </div>
      }
    >
      <p className="mb-3 text-sm text-ink-500">The amount goes back to your original payment method in 5–7 working days.</p>
      <ul className="space-y-2">
        {cancellable.map((i) => {
          const on = pick[i.id] > 0;
          return (
            <li key={i.id} className={`flex items-center gap-3 rounded-2xl border p-3 ${on ? 'border-danger/40 bg-danger-tint/40' : 'border-ink-200'}`}>
              <button
                type="button"
                role="checkbox"
                aria-checked={on}
                aria-label={`Cancel ${i.title}`}
                onClick={() => setPick((p) => ({ ...p, [i.id]: on ? 0 : activeQty(i) }))}
                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-[5px] border ${on ? 'border-danger bg-danger' : 'border-ink-400 bg-white'}`}
              >
                {on && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
              </button>
              <Img src={i.image} alt="" className="h-14 w-11 shrink-0 rounded-lg" />
              <div className="min-w-0 flex-1 text-sm">
                <p className="line-clamp-1 text-ink-900">{i.title}</p>
                <p className="text-xs text-ink-500">{i.variantLabel} · {rupees(i.unitPrice)} each</p>
              </div>
              {activeQty(i) > 1 && on && (
                <QtyStepper size="sm" value={pick[i.id]} min={1} max={activeQty(i)} onChange={(q) => setPick((p) => ({ ...p, [i.id]: q }))} />
              )}
            </li>
          );
        })}
      </ul>
    </Sheet>
  );
};

/* -------------------------------------------------------------- Return */

export const ReturnSheet = ({ open, onClose, item, onDone }) => {
  const [qty, setQty] = useState(1);
  const [reason, setReason] = useState('');
  const [comment, setComment] = useState('');
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setQty(1);
      setReason('');
      setComment('');
      setPhotos([]);
    }
  }, [open]);

  if (!item) return null;
  const max = item.return?.qty || 1;

  const addPhotos = async (files) => {
    const list = Array.from(files || []).slice(0, 6 - photos.length);
    if (!list.length) return;
    setUploading(true);
    for (const f of list) {
      if (!f.type.startsWith('image/')) continue;
      try {
        const a = await uploadAttachment(f);
        setPhotos((p) => [...p, a.url]);
      } catch (err) {
        toast.error(friendlyError(err, `Couldn't upload ${f.name}`));
      }
    }
    setUploading(false);
  };

  const submit = async () => {
    if (!reason) {
      toast.error('Pick a reason for the return');
      return;
    }
    setBusy(true);
    try {
      await requestReturn({
        orderItemId: item.id,
        qty,
        reason,
        ...(comment.trim() ? { comment: comment.trim() } : {}),
        ...(photos.length ? { images: photos.filter((u) => u.length <= 255) } : {}),
      });
      toast.success('Return requested. The seller will arrange a pickup.');
      onDone();
      onClose();
    } catch (err) {
      toast.error(friendlyError(err, 'Return could not be requested'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Return item"
      footer={
        <button type="button" onClick={submit} disabled={busy || uploading || !reason} className="lk-btn-primary w-full">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          Request return · refund {rupees(item.unitPrice * qty)}
        </button>
      }
    >
      <div className="flex items-center gap-3 rounded-2xl bg-surface-muted p-3">
        <Img src={item.image} alt="" className="h-16 w-12 shrink-0 rounded-lg" />
        <div className="min-w-0 flex-1 text-sm">
          <p className="line-clamp-2 text-ink-900">{item.title}</p>
          <p className="text-xs text-ink-500">
            {item.variantLabel} · {item.return?.daysLeft} day{item.return?.daysLeft === 1 ? '' : 's'} left to return
          </p>
        </div>
      </div>

      {max > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <span className="text-sm font-medium text-ink-900">Quantity to return</span>
          <QtyStepper size="sm" value={qty} max={max} onChange={setQty} />
        </div>
      )}

      <p className="mt-5 text-sm font-medium text-ink-900">Why are you returning this?</p>
      <div className="mt-2 space-y-1.5" role="radiogroup" aria-label="Return reason">
        {RETURN_REASONS.map((r) => (
          <button
            key={r.value}
            type="button"
            role="radio"
            aria-checked={reason === r.value}
            onClick={() => setReason(r.value)}
            className={`flex min-h-[44px] w-full items-center gap-3 rounded-xl border px-3 text-left text-sm transition ${
              reason === r.value ? 'border-brand-secondary bg-brand-tint/60 font-medium text-ink-900' : 'border-ink-200 text-ink-700 hover:border-ink-400'
            }`}
          >
            <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 ${reason === r.value ? 'border-brand-secondary' : 'border-ink-400'}`}>
              {reason === r.value && <span className="h-2 w-2 rounded-full bg-brand-secondary" />}
            </span>
            {r.label}
          </button>
        ))}
      </div>

      <label className="mt-5 block">
        <span className="text-sm font-medium text-ink-900">Tell us more (optional)</span>
        <textarea value={comment} onChange={(e) => setComment(e.target.value)} maxLength={1000} rows={3} className="lk-input mt-1.5 resize-none" placeholder="What's wrong with it?" />
      </label>

      <div className="mt-4">
        <span className="text-sm font-medium text-ink-900">Photos (helps the seller approve faster)</span>
        <div className="mt-2 flex flex-wrap gap-2">
          {photos.map((u) => (
            <span key={u} className="relative">
              <img src={u} alt="" className="h-16 w-16 rounded-lg object-cover ring-1 ring-ink-200" />
              <button type="button" aria-label="Remove photo" onClick={() => setPhotos((p) => p.filter((x) => x !== u))} className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-ink-900 text-white">
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          {photos.length < 6 && (
            <label className="flex h-16 w-16 cursor-pointer flex-col items-center justify-center gap-0.5 rounded-lg border border-dashed border-ink-400 text-[10px] text-ink-500 hover:bg-surface-muted">
              {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
              Add
              <input type="file" accept="image/*" multiple className="sr-only" onChange={(e) => addPhotos(e.target.files)} />
            </label>
          )}
        </div>
      </div>
    </Sheet>
  );
};

/* -------------------------------------------------------------- Review */

const RATING_WORDS = ['', 'Terrible', 'Bad', 'Okay', 'Good', 'Loved it'];

export const ReviewSheet = ({ open, onClose, item, onDone }) => {
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);

  const existing = item?.review || null;

  // Editing starts from what they wrote last time, not a blank form.
  useEffect(() => {
    if (open) {
      setRating(Number(existing?.rating) || 0);
      setTitle(existing?.title || '');
      setBody(existing?.body || '');
    }
  }, [open, existing]);

  if (!item) return null;

  const submit = async () => {
    setBusy(true);
    try {
      // Empty fields are left out; the backend keeps what was not sent, so an
      // edit that only changes the stars never wipes the text.
      const res = await submitReview({
        orderItemId: item.id,
        rating,
        ...(title.trim() ? { title: title.trim() } : {}),
        ...(body.trim() ? { body: body.trim() } : {}),
      });
      toast.success(res?.message || 'Thanks for your review');
      onDone();
      onClose();
    } catch (err) {
      toast.error(friendlyError(err, 'Review could not be saved'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={existing ? 'Edit your review' : 'Rate this product'}
      footer={
        <button type="button" onClick={submit} disabled={busy || !rating} className="lk-btn-primary w-full">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          {existing ? 'Update review' : 'Submit review'}
        </button>
      }
    >
      {existing?.status && existing.status !== 'published' && (
        <p className="mb-3 rounded-xl bg-warning-tint px-3 py-2 text-xs text-warning">
          {existing.status === 'hidden' ? 'Your review is hidden from the product page.' : 'Your review is waiting for a check before it shows on the product page.'}
        </p>
      )}
      <div className="flex items-center gap-3">
        <Img src={item.image} alt="" className="h-16 w-12 shrink-0 rounded-lg" />
        <p className="line-clamp-2 text-sm text-ink-900">{item.title}</p>
      </div>
      <div className="mt-5 flex flex-col items-center gap-1">
        <Stars value={rating} onChange={setRating} size="h-9 w-9" label="Your rating" />
        <p className="h-5 text-sm font-semibold text-ink-700">{RATING_WORDS[rating]}</p>
      </div>
      <label className="mt-4 block">
        <span className="text-sm font-medium text-ink-900">Headline (optional)</span>
        <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} className="lk-input mt-1.5" placeholder="Sum it up in a few words" />
      </label>
      <label className="mt-3 block">
        <span className="text-sm font-medium text-ink-900">Your review (optional)</span>
        <textarea value={body} onChange={(e) => setBody(e.target.value)} maxLength={4000} rows={4} className="lk-input mt-1.5 resize-none" placeholder="What did you like or dislike? How was the fit, quality, taste?" />
      </label>
    </Sheet>
  );
};
